import { create } from "zustand";
import { persist } from "zustand/middleware";

type Theme = "light" | "dark" | "system";

export type Language = "pt-BR" | "en" | "es";

interface UiState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  language: Language;
  setLanguage: (language: Language) => void;
  /** Painel lateral do chat (1:1 e team session). Persiste no orbit:ui
   *  para que a escolha sobreviva a refreshes. */
  chatSidePanelCollapsed: boolean;
  toggleChatSidePanel: () => void;
  setChatSidePanelCollapsed: (collapsed: boolean) => void;
  /** Sidebar interno de historico do /chat (M8). Persiste para que a
   *  escolha do user (recolhido/expandido) sobreviva a refreshes. */
  chatHistoryCollapsed: boolean;
  toggleChatHistory: () => void;
  /** Toggle "Mostrar uso de ferramentas" do composer do chat. Default
   *  false = UI limpa (baloes de tool_call/tool_result escondidos em
   *  todos os chats). Persiste no orbit:ui. */
  chatShowToolUsage: boolean;
  toggleChatShowToolUsage: () => void;
  setChatShowToolUsage: (show: boolean) => void;
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const resolved =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: "system",
      sidebarCollapsed: false,
      language: "pt-BR",
      chatSidePanelCollapsed: false,
      chatHistoryCollapsed: false,
      chatShowToolUsage: false,
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleSidebar: () =>
        set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setLanguage: (language) => set({ language }),
      toggleChatSidePanel: () =>
        set({ chatSidePanelCollapsed: !get().chatSidePanelCollapsed }),
      setChatSidePanelCollapsed: (chatSidePanelCollapsed) =>
        set({ chatSidePanelCollapsed }),
      toggleChatHistory: () =>
        set({ chatHistoryCollapsed: !get().chatHistoryCollapsed }),
      toggleChatShowToolUsage: () =>
        set({ chatShowToolUsage: !get().chatShowToolUsage }),
      setChatShowToolUsage: (chatShowToolUsage) =>
        set({ chatShowToolUsage }),
    }),
    {
      name: "orbit:ui",
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

// Aplica imediatamente caso store ainda nao tenha rehidratado
if (typeof window !== "undefined") {
  applyTheme(useUiStore.getState().theme);
}
